import { getDateForAttendee } from '@eventbrite/event-date-time';
import { History } from 'history';
import { AnyAction, Dispatch } from 'redux';
import {
    mapEventDatesToCompactCalendarData,
    mapEventDatesToScrollCalendarData,
} from '../components/TimedEntrySeries/CalendarContainer/utils';
import { getAppLocale, getIsAppRebrandActive } from '../selectors/app';
import { getIsEventOnline } from '../selectors/event';
import {
    getActiveSection,
    getCalendarDates,
    getCalendarType,
    getScrollCalendarData,
    getTimezone,
} from '../selectors/infiniteCalendarSeries/infiniteCalendarSeries';
import { CheckoutState } from '../state';
import {
    AccordionSection,
    CalendarType,
    CompactCalendarData,
    EventDate,
    ParentEventInfo,
    ScrollCalendarData,
    TimeSlot,
} from '../state/infiniteCalendarSeries';
import { normalizeLocale } from '../utils/general';
import { mapSessionsToTimeSlots } from './dateSessions';

export enum InfiniteCalendarSeriesActionType {
    setActiveSection = 'setActiveSection',
    setCollapsedSectionsHeight = 'setCollapsedSectionsHeight',
    setParentEventInfo = 'setParentEventInfo',
    setTimeSectionLoading = 'setTimeSectionLoading',
    setTicketSectionLoading = 'setTicketSectionLoading',
    setShouldShowTimeSection = 'setShouldShowTimeSection',
    setSelectedDate = 'setSelectedDate',
    setSelectedTime = 'setSelectedTime',
    setSelectedSessionId = 'setSelectedSessionId',
    setShouldShowTimeError = 'setShouldShowTimeError',
    setShouldShowTicketError = 'setShouldShowTicketError',
    setCalendarType = 'setCalendarType',
    setScrollCalendarData = 'setScrollCalendarData',
    setCompactCalendarData = 'setCompactCalendarData',
    setTimeSlots = 'setTimeSlots',
    setCalendarDates = 'setCalendarDates',
}

export interface SetSectionAction {
    type: InfiniteCalendarSeriesActionType.setActiveSection;
    payload: AccordionSection | null;
}

export interface SetCollapsedSectionsHeightAction {
    type: InfiniteCalendarSeriesActionType.setCollapsedSectionsHeight;
    payload: number;
}

export interface SetParentEventInfoAction {
    type: InfiniteCalendarSeriesActionType.setParentEventInfo;
    payload: ParentEventInfo;
}

export interface SetTimeSectionLoadingAction {
    type: InfiniteCalendarSeriesActionType.setTimeSectionLoading;
    payload: boolean;
}

export interface SetTicketSectionLoadingAction {
    type: InfiniteCalendarSeriesActionType.setTicketSectionLoading;
    payload: boolean;
}

export interface SetShouldShowTimeSectionAction {
    type: InfiniteCalendarSeriesActionType.setShouldShowTimeSection;
    payload: boolean;
}

export interface SetShouldShowTimeErrorAction {
    type: InfiniteCalendarSeriesActionType.setShouldShowTimeError;
    payload: boolean;
}

export interface SetShouldShowTicketErrorAction {
    type: InfiniteCalendarSeriesActionType.setShouldShowTicketError;
    payload: boolean;
}

export interface SetSelectedDateAction {
    type: InfiniteCalendarSeriesActionType.setSelectedDate;
    payload: string;
}

export interface SetSelectedTimeAction {
    type: InfiniteCalendarSeriesActionType.setSelectedTime;
    payload: string;
}

export interface SetScrollCalendarDataAction {
    type: InfiniteCalendarSeriesActionType.setScrollCalendarData;
    payload: ScrollCalendarData;
}

export interface SetCompactCalendarDataAction {
    type: InfiniteCalendarSeriesActionType.setCompactCalendarData;
    payload: CompactCalendarData;
}

export interface SetSelectedSessionIdAction {
    type: InfiniteCalendarSeriesActionType.setSelectedSessionId;
    payload: string;
}

export interface SetCalendarTypeAction {
    type: InfiniteCalendarSeriesActionType.setCalendarType;
    payload: CalendarType;
}

export interface SetTimeSlotsAction {
    type: InfiniteCalendarSeriesActionType.setTimeSlots;
    payload: TimeSlot[];
}

export interface SetCalendarDatesAction {
    type: InfiniteCalendarSeriesActionType.setCalendarDates;
    payload: EventDate[];
}

export interface SetParentEventInfoAction {
    type: InfiniteCalendarSeriesActionType.setParentEventInfo;
    payload: ParentEventInfo;
}

export type InfiniteCalendarSeriesAction =
    | SetCalendarDatesAction
    | SetCalendarTypeAction
    | SetCollapsedSectionsHeightAction
    | SetCompactCalendarDataAction
    | SetParentEventInfoAction
    | SetScrollCalendarDataAction
    | SetSectionAction
    | SetSelectedDateAction
    | SetSelectedSessionIdAction
    | SetSelectedTimeAction
    | SetShouldShowTicketErrorAction
    | SetShouldShowTimeErrorAction
    | SetShouldShowTimeSectionAction
    | SetTicketSectionLoadingAction
    | SetTimeSectionLoadingAction
    | SetTimeSlotsAction;

export interface InfiniteCalendarSeriesDispatcher {
    onDateSelect: (
        dateString: string,
        calendarType: CalendarType,
        id?: string,
        sessionTime?: string,
    ) => void;
    onTimeSelect: (
        sessionId: string,
        sessionLabel: string,
        calendarType?: CalendarType,
    ) => void;
    onMonthLoad: (month: number, year: number, timezone: string) => void;
    onInitialLoad: () => void;
    onToggleSection: (params: {
        activeSection: AccordionSection;
        selectedDate?: string;
        selectedTime?: string;
        calendarType?: CalendarType;
    }) => void;
    resetTimeSelection: () => void;
    onPageClose: (dispatch: Dispatch<AnyAction>, router: History) => void;
}

type CalendarConfiguration = {
    activeSection: AccordionSection | null;
    calendarType: CalendarType;
    calendarDates: EventDate[];
    isAppRebrandActive: boolean;
    isOnlineEvent: boolean;
    locale: string;
    timezone: string;
    previousScrollCalendarData: ScrollCalendarData;
};

export const setSection = (
    accordionName: AccordionSection | null,
): SetSectionAction => ({
    type: InfiniteCalendarSeriesActionType.setActiveSection,
    payload: accordionName,
});

export const setParentEventInfo = (parentEventInfo: ParentEventInfo) => ({
    type: InfiniteCalendarSeriesActionType.setParentEventInfo,
    payload: parentEventInfo,
});

export const setTimeSectionLoading = (isLoading: boolean) => ({
    type: InfiniteCalendarSeriesActionType.setTimeSectionLoading,
    payload: isLoading,
});

export const setTicketSectionLoading = (isLoading: boolean) => ({
    type: InfiniteCalendarSeriesActionType.setTicketSectionLoading,
    payload: isLoading,
});

export const setShouldShowTimeSection = (shouldShow: boolean) => ({
    type: InfiniteCalendarSeriesActionType.setTicketSectionLoading,
    payload: shouldShow,
});

export const setShouldShowTimeError = (shouldShowError: boolean) => ({
    type: InfiniteCalendarSeriesActionType.setShouldShowTimeError,
    payload: shouldShowError,
});

export const setShouldShowTicketError = (shouldShowError: boolean) => ({
    type: InfiniteCalendarSeriesActionType.setShouldShowTicketError,
    payload: shouldShowError,
});

export const setSelectedDate = (selectedDate: string) => ({
    type: InfiniteCalendarSeriesActionType.setSelectedDate,
    payload: selectedDate,
});

export const setSelectedTime = (selectedTime: string) => ({
    type: InfiniteCalendarSeriesActionType.setSelectedTime,
    payload: selectedTime,
});

export const setScrollCalendarData = (calendarData: ScrollCalendarData) => ({
    type: InfiniteCalendarSeriesActionType.setScrollCalendarData,
    payload: calendarData,
});

export const setCompactCalendarData = (calendarData: CompactCalendarData) => ({
    type: InfiniteCalendarSeriesActionType.setCompactCalendarData,
    payload: calendarData,
});

export const setCollapsedSectionsHeight = (
    collapsedSectionsHeight: number,
) => ({
    type: InfiniteCalendarSeriesActionType.setCollapsedSectionsHeight,
    payload: collapsedSectionsHeight,
});

export const setSelectedSessionId = (selectedSessionId: string) => ({
    type: InfiniteCalendarSeriesActionType.setSelectedSessionId,
    payload: selectedSessionId,
});

export const setCalendarType = (calendarType: CalendarType) => ({
    type: InfiniteCalendarSeriesActionType.setCalendarType,
    payload: calendarType,
});

export const setTimeSlots = (timeSlots: TimeSlot[]) => ({
    type: InfiniteCalendarSeriesActionType.setTimeSlots,
    payload: timeSlots,
});

export const setCalendarDates = (calendarDates: EventDate[]) => ({
    type: InfiniteCalendarSeriesActionType.setCalendarDates,
    payload: calendarDates,
});

const _getCalendarConfiguration = (
    state: CheckoutState,
): CalendarConfiguration => ({
    activeSection: getActiveSection(state),
    calendarType: getCalendarType(state),
    calendarDates: getCalendarDates(state),
    isAppRebrandActive: getIsAppRebrandActive(state),
    isOnlineEvent: getIsEventOnline(state),
    locale: normalizeLocale(getAppLocale(state)),
    timezone: getTimezone(state),
    previousScrollCalendarData: getScrollCalendarData(state) || {},
});

const _initializeCompactCalendar = (
    dispatch: Dispatch<AnyAction>,
    config: CalendarConfiguration,
) => {
    const {
        calendarDates,
        isAppRebrandActive,
        locale,
        timezone,
        isOnlineEvent,
    } = config;
    dispatch(
        setCompactCalendarData(
            mapEventDatesToCompactCalendarData(
                calendarDates,
                timezone,
                locale,
                isAppRebrandActive,
                isOnlineEvent,
            ),
        ),
    );
};

const _initializeSingleDayEvents = (
    dispatch: Dispatch<AnyAction>,
    config: CalendarConfiguration,
) => {
    const { calendarDates, activeSection } = config;
    if (calendarDates?.length) {
        const { sessions, date } = calendarDates[0];
        dispatch(setSelectedDate(date));
        dispatch(setTimeSlots(mapSessionsToTimeSlots(sessions || [])));
        if (activeSection !== AccordionSection.tickets) {
            dispatch(setSection(AccordionSection.time));
        }
    }
};

const _initializeScrollCalendar = (
    dispatch: Dispatch<AnyAction>,
    config: CalendarConfiguration,
) => {
    const {
        calendarDates,
        isOnlineEvent,
        previousScrollCalendarData,
        timezone,
    } = config;
    if (calendarDates?.length) {
        const date = getDateForAttendee({
            isOnlineEvent,
            dateAndTimezone: {
                utc: calendarDates[0].date,
                timezone,
            },
        });
        const scrollCalendarData = mapEventDatesToScrollCalendarData({
            eventDates: calendarDates,
            previousScrollCalendarData,
            month: date.month(),
            year: date.year(),
            timezone,
            isOnlineEvent,
        });
        dispatch(setScrollCalendarData(scrollCalendarData));
    }
};

const calendarInitializerMap: Record<
    CalendarType,
    (dispatch: Dispatch<AnyAction>, config: CalendarConfiguration) => void
> = {
    [CalendarType.compactWithSession]: _initializeCompactCalendar,
    [CalendarType.compactWithoutSession]: _initializeCompactCalendar,
    [CalendarType.singleDayEvents]: _initializeSingleDayEvents,
    [CalendarType.scrollCalendar]: _initializeScrollCalendar,
};

export const initializeInfiniteCalendarSeries = (
    dispatch: Dispatch<AnyAction>,
    getState: () => CheckoutState,
) => {
    const state = getState();
    const config = _getCalendarConfiguration(state);
    if (config.calendarDates && config.calendarType) {
        calendarInitializerMap[config.calendarType]?.(dispatch, config);
        dispatch(setCalendarType(config.calendarType));
    }
};
